import PropTypes from "prop-types";
import { theme } from "../../../theme";
import Button from "../../Button/Button";
import Typography from "../../Typography";
import HelmetIcon from "../../Icons/HelmetIcon";
import styled from "styled-components";
import {
	AccountBlock,
	AvatarPlaceholder,
	BlockHeader,
	BlockHeaderContent,
	NotificationRowCopy,
	PrefsForm,
	SettingsBlock,
	SettingsLayout,
	SettingsLayoutVertical,
	SettingsLayoutActions,
	UserAvatar,
	UserDescription,
	AccountSection,
} from "./UserSettingsTabPrimitives";

function UserSettingsAccountTab({
	t,
	user,
	getInterestsSummary,
	onEditProfile,
	onEditInterests,
	onDeleteAccount,
}) {
	return (
		<SettingsBlock>
			<BlockHeader>
				<BlockHeaderContent>
					<Typography
						as="h2"
						$variant="title-5-semibold"
						$variantMobile="body-1-medium"
						$variantTablet="body-1-medium"
					>
						{t("settings.account.title")}
					</Typography>
				</BlockHeaderContent>
			</BlockHeader>
			<PrefsForm>
				<AccountSection>
					<SettingsLayoutVertical>
						{/* User profile */}
						<NotificationRowCopy>
							<Typography as="span" $variant="body-1-semibold">
								{t("settings.account.profile.title")}
							</Typography>
							<Typography
								as="span"
								$variant="body-2-medium"
								color={theme.colors.defaultWeak}
							>
								{t("settings.account.profile.summary")}
							</Typography>
						</NotificationRowCopy>
						{/* User info block */}
						{user && (
							<AccountProfileCard>
								<AccountBlock>
									{user.userAvatar ? (
										<UserAvatar src={user.userAvatar} alt="user avatar" />
									) : (
										<AvatarPlaceholder>
											<HelmetIcon
												width={40}
												height={40}
												fill={theme.colors.defaultStrong}
											/>
										</AvatarPlaceholder>
									)}
									<UserDescription>
										<Typography as="p" $variant="body-1-semibold">
											{user.name} {user.lastName}
										</Typography>
										<Typography
											as="p"
											color={theme.colors.defaultWeak}
											$variant="body-2-medium"
										>
											{user.email}
										</Typography>
									</UserDescription>
								</AccountBlock>
								<SettingsLayoutActions>
									<Button
										$variant="outline"
										onClick={onEditProfile}
										size="l"
										sizeTablet="m"
										sizeMobile="m"
									>
										{t("settings.account.editProfile")}
									</Button>
								</SettingsLayoutActions>
							</AccountProfileCard>
						)}
					</SettingsLayoutVertical>

					<SettingsLayout>
						<NotificationRowCopy>
							<Typography as="span" $variant="body-1-semibold">
								{t("settings.account.interests.title")}
							</Typography>
							<Typography
								as="span"
								$variant="body-2-medium"
								color={theme.colors.defaultWeak}
							>
								{getInterestsSummary()}
							</Typography>
						</NotificationRowCopy>
						<SettingsLayoutActions>
							<Button
								$variant="outline"
								onClick={onEditInterests}
								size="l"
								sizeTablet="m"
								sizeMobile="m"
							>
								{t("settings.account.interests.action")}
							</Button>
						</SettingsLayoutActions>
					</SettingsLayout>

					<SettingsLayout>
						<NotificationRowCopy>
							<Typography
								as="span"
								$variant="body-1-semibold"
								color={theme.colors.errorMain}
							>
								{t("settings.account.deleteAccount")}
							</Typography>
							<Typography
								as="span"
								$variant="body-2-medium"
								color={theme.colors.defaultWeak}
							>
								{t("settings.dangerZone.subtitle")}
							</Typography>
						</NotificationRowCopy>
						<SettingsLayoutActions>
							<Button
								$variant="outlineDanger"
								onClick={onDeleteAccount}
								size="l"
								sizeTablet="m"
								sizeMobile="m"
							>
								{t("settings.account.deleteAccount")}
							</Button>
						</SettingsLayoutActions>
					</SettingsLayout>
				</AccountSection>
			</PrefsForm>
		</SettingsBlock>
	);
}

UserSettingsAccountTab.propTypes = {
	t: PropTypes.func.isRequired,
	user: PropTypes.object,
	getInterestsSummary: PropTypes.func.isRequired,
	onEditProfile: PropTypes.func.isRequired,
	onEditInterests: PropTypes.func.isRequired,
	onDeleteAccount: PropTypes.func.isRequired,
};

export default UserSettingsAccountTab;

const AccountProfileCard = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	justify-content: space-between;
	gap: ${({ theme }) => theme.sizing.md};
	flex-wrap: wrap;

	@media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
		flex-direction: column;
		align-items: flex-start;
	}
`;
